import { useState, useEffect } from "react";
import { Link as InertiaLink, router } from "@inertiajs/react";
import {
  Card,
  CardBody,
  CardHeader,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Chip,
  Link,
  Tooltip,
  Divider,
  Progress,
  useDisclosure,
} from "@heroui/react";
import Layout from "../Layouts/Layout";
import { subscribeToRunUpdates } from "../../channels/run_channel";

export default function Show({ run }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [localRun, setLocalRun] = useState(run);

  useEffect(() => {
    const subscription = subscribeToRunUpdates((data) => {
      if (data.run_id === localRun.id) {
        setLocalRun((prevRun) => ({
          ...prevRun,
          status: data.status,
          output: data.output || prevRun.output,
          error_message: data.error || prevRun.error_message,
          input_tokens: data.tokens?.input || prevRun.input_tokens,
          output_tokens: data.tokens?.output || prevRun.output_tokens,
        }));
      }
    });

    return () => subscription.unsubscribe();
  }, [localRun.id]);

  const confirmDelete = () => {
    router.delete(`/runs/${localRun.id}`);
    onClose();
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "success";
      case "failed":
        return "danger";
      case "processing":
        return "warning";
      default:
        return "default";
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const calculateDuration = () => {
    if (!localRun.started_at || !localRun.completed_at) return null;
    const start = new Date(localRun.started_at);
    const end = new Date(localRun.completed_at);
    const diffMs = end - start;
    if (diffMs < 1000) return `${diffMs}ms`;
    if (diffMs < 60000) return `${(diffMs / 1000).toFixed(2)}s`;
    return `${(diffMs / 60000).toFixed(2)}m`;
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link
            as={InertiaLink}
            href="/runs"
            className="inline-flex items-center gap-1 text-gray-600 dark:text-gray-400 hover:text-amber-600 dark:hover:text-amber-400 transition-colors mb-4"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to Runs
          </Link>

          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                  Run details
                </h1>
                <Chip
                  color={getStatusColor(localRun.status)}
                  variant="flat"
                  size="md"
                  startContent={
                    localRun.status === "processing" ? (
                      <span className="w-2 h-2 bg-current rounded-full animate-pulse" />
                    ) : null
                  }
                >
                  {localRun.status}
                </Chip>
              </div>
              <Link
                as={InertiaLink}
                href={`/tasks/${localRun.task.id}`}
                className="text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 font-medium"
              >
                Task: {localRun.task.name}
              </Link>
            </div>
            <Button
              onPress={onOpen}
              color="danger"
              variant="flat"
              startContent={
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              }
            >
              Delete run
            </Button>
          </div>
        </div>

        {localRun.status === "processing" && (
          <Card className="mb-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200/50 dark:border-amber-800/50">
            <CardBody className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <svg
                  className="w-5 h-5 text-amber-600 dark:text-amber-400 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <p className="font-medium text-amber-800 dark:text-amber-300">
                  Task is currently processing...
                </p>
              </div>
              <Progress size="sm" isIndeterminate color="warning" />
            </CardBody>
          </Card>
        )}

        <div className="space-y-6">
          {/* Input */}
          <Card className="bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50">
            <CardHeader className="flex justify-between items-center pb-2">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                Input
              </h3>
              <Tooltip content="Copy input">
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  onPress={() => copyToClipboard(localRun.input)}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                </Button>
              </Tooltip>
            </CardHeader>
            <CardBody className="pt-0">
              <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 whitespace-pre-wrap text-sm font-mono text-gray-700 dark:text-gray-300 max-h-96 overflow-y-auto">
                {localRun.input}
              </div>
            </CardBody>
          </Card>

          {/* Output */}
          {localRun.status === "completed" && localRun.output && (
            <Card className="bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50">
              <CardHeader className="flex justify-between items-center pb-2">
                <h3 className="text-sm font-semibold text-green-600 dark:text-green-400 uppercase tracking-wider">
                  Output
                </h3>
                <Tooltip content="Copy output">
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    onPress={() => copyToClipboard(localRun.output)}
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                  </Button>
                </Tooltip>
              </CardHeader>
              <CardBody className="pt-0">
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 whitespace-pre-wrap text-sm font-mono text-gray-700 dark:text-gray-300 max-h-96 overflow-y-auto">
                  {localRun.output}
                </div>
              </CardBody>
            </Card>
          )}

          {/* Error */}
          {localRun.status === "failed" && localRun.error_message && (
            <Card className="bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm border border-red-200/50 dark:border-red-800/50">
              <CardHeader className="pb-2">
                <h3 className="text-sm font-semibold text-red-600 dark:text-red-400 uppercase tracking-wider">
                  Error
                </h3>
              </CardHeader>
              <CardBody className="pt-0">
                <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 text-sm font-mono text-red-700 dark:text-red-300">
                  {localRun.error_message}
                </div>
              </CardBody>
            </Card>
          )}

          {/* Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Timestamps */}
            <Card className="bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50">
              <CardHeader className="pb-2">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                  Timestamps
                </h3>
              </CardHeader>
              <CardBody className="pt-0">
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Created
                    </span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {formatDate(localRun.created_at)}
                    </span>
                  </div>
                  {localRun.started_at && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Started
                      </span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {formatDate(localRun.started_at)}
                      </span>
                    </div>
                  )}
                  {localRun.completed_at && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Completed
                      </span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {formatDate(localRun.completed_at)}
                      </span>
                    </div>
                  )}
                  {calculateDuration() && (
                    <>
                      <Divider />
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          Duration
                        </span>
                        <Chip color="primary" variant="flat" size="sm">
                          {calculateDuration()}
                        </Chip>
                      </div>
                    </>
                  )}
                </div>
              </CardBody>
            </Card>

            {/* Usage */}
            <Card className="bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50">
              <CardHeader className="pb-2">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                  Token usage
                </h3>
              </CardHeader>
              <CardBody className="pt-0">
                {localRun.input_tokens ? (
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Input tokens
                      </span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {localRun.input_tokens.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Output tokens
                      </span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {localRun.output_tokens.toLocaleString()}
                      </span>
                    </div>
                    <Divider />
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        Total tokens
                      </span>
                      <Chip color="warning" variant="flat" size="sm">
                        {(
                          localRun.input_tokens + localRun.output_tokens
                        ).toLocaleString()}
                      </Chip>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                    Token usage data not available
                  </p>
                )}
              </CardBody>
            </Card>
          </div>

          {/* Task Details */}
          {localRun.task_details && (
            <Card className="bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50">
              <CardHeader className="pb-2">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                  Task configuration snapshot
                </h3>
              </CardHeader>
              <CardBody className="pt-0">
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                        Instructions
                      </h4>
                      <Tooltip content="Copy instructions">
                        <Button
                          isIconOnly
                          size="sm"
                          variant="light"
                          onPress={() =>
                            copyToClipboard(localRun.task_details.instructions)
                          }
                        >
                          <svg
                            className="w-3.5 h-3.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                            />
                          </svg>
                        </Button>
                      </Tooltip>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3 whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300 max-h-48 overflow-y-auto">
                      {localRun.task_details.instructions}
                    </div>
                  </div>

                  {localRun.task_details.rules && (
                    <div>
                      <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">
                        Rules
                      </h4>
                      <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3 whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300 max-h-48 overflow-y-auto">
                        {localRun.task_details.rules}
                      </div>
                    </div>
                  )}

                  {localRun.task_details.system_prompt && (
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                          System Prompt
                        </h4>
                        <Tooltip content="Copy system prompt">
                          <Button
                            isIconOnly
                            size="sm"
                            variant="light"
                            onPress={() =>
                              copyToClipboard(
                                localRun.task_details.system_prompt
                              )
                            }
                          >
                            <svg
                              className="w-3.5 h-3.5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                              />
                            </svg>
                          </Button>
                        </Tooltip>
                      </div>
                      <pre className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3 text-xs font-mono text-gray-700 dark:text-gray-300 max-h-48 overflow-auto">
                        {localRun.task_details.system_prompt}
                      </pre>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          )}
        </div>
      </div>

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        classNames={{
          base: "bg-white dark:bg-gray-800",
          header: "border-b border-gray-200 dark:border-gray-700",
          footer: "border-t border-gray-200 dark:border-gray-700",
        }}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                <div className="flex items-center gap-2 text-red-600">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  Delete Run
                </div>
              </ModalHeader>
              <ModalBody>
                <p className="text-gray-700 dark:text-gray-300">
                  Are you sure you want to delete this run?
                </p>
                <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 mt-3">
                  <p className="text-sm text-red-700 dark:text-red-300">
                    This action cannot be undone. All run data including input,
                    output, and usage statistics will be permanently deleted.
                  </p>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button variant="light" onPress={onClose}>
                  Cancel
                </Button>
                <Button
                  color="danger"
                  onPress={confirmDelete}
                  startContent={
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  }
                >
                  Delete run
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </Layout>
  );
}
